import { Slot } from '@radix-ui/react-slot';
import type {
  ComponentPropsWithoutRef,
  CSSProperties,
  HTMLAttributes,
  ReactNode,
} from 'react';
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useId,
  useMemo,
  useState,
} from 'react';
import { createPortal } from 'react-dom';

import { cn } from '../../lib/utils';
import { msg } from '../../localization/i18n';
import { DialogPortalContainerContext } from '../TovDialogPortalContext/TovDialogPortalContext';
import { TovButton, TovIconButton } from '../TovButton/TovButton';

const DialogContext = createContext<{
  contentId: string;
  descriptionId: string;
  open: boolean;
  portalContainer: HTMLElement | null;
  setPortalContainer(node: HTMLElement | null): void;
  setOpen(nextOpen: boolean): void;
  titleId: string;
} | null>(null);

export function Dialog({
  bodyClassName,
  children,
  contentClassName,
  defaultOpen = false,
  description,
  footer,
  footerClassName,
  footerChildren,
  footerStart,
  headerChildren,
  hasCloseButton = true,
  headerClassName,
  layout = 'default',
  onOpenChange,
  open,
  title,
  trigger,
}: {
  bodyClassName?: string;
  children?: ReactNode;
  contentClassName?: string;
  defaultOpen?: boolean;
  description?: ReactNode;
  footer?: ReactNode;
  footerClassName?: string;
  footerChildren?: ReactNode;
  footerStart?: ReactNode;
  headerChildren?: ReactNode;
  hasCloseButton?: boolean;
  headerClassName?: string;
  layout?: 'default' | 'page';
  onOpenChange?(open: boolean): void;
  open?: boolean;
  title?: ReactNode;
  trigger?: ReactNode;
}) {
  const [internalOpen, setInternalOpen] = useState(defaultOpen);
  const [portalContainer, setPortalContainer] = useState<HTMLElement | null>(
    null,
  );
  const handlePortalContainerRef = useCallback((node: HTMLElement | null) => {
    setPortalContainer((current) => (current === node ? current : node));
  }, []);
  const contentId = useId();
  const descriptionId = useId();
  const titleId = useId();
  const resolvedOpen = open ?? internalOpen;

  const setOpen = useCallback(
    (nextOpen: boolean) => {
      if (open === undefined) {
        setInternalOpen(nextOpen);
      }

      onOpenChange?.(nextOpen);
    },
    [onOpenChange, open],
  );
  const contextValue = useMemo(
    () => ({
      contentId,
      descriptionId,
      open: resolvedOpen,
      portalContainer,
      setPortalContainer: handlePortalContainerRef,
      setOpen,
      titleId,
    }),
    [
      contentId,
      descriptionId,
      handlePortalContainerRef,
      portalContainer,
      resolvedOpen,
      setOpen,
      titleId,
    ],
  );

  const isStructuredDialog =
    trigger !== undefined ||
    headerChildren !== undefined ||
    title !== undefined ||
    description !== undefined ||
    footer !== undefined ||
    footerChildren !== undefined ||
    footerStart !== undefined ||
    contentClassName !== undefined ||
    bodyClassName !== undefined ||
    headerClassName !== undefined ||
    layout !== 'default';
  const resolvedContentClassName =
    layout === 'page'
      ? cn('dashboard-dialog-page', contentClassName)
      : contentClassName;
  const resolvedBodyClassName =
    layout === 'page'
      ? cn('dashboard-dialog-page__body', bodyClassName)
      : bodyClassName;
  const resolvedFooter =
    footerStart !== undefined || (layout === 'page' && footer !== undefined) ? (
      <DialogPageFooter
        className={footerClassName}
        end={footer}
        start={footerStart}
      />
    ) : footer !== undefined ? (
      <DialogFooter className={footerClassName}>{footer}</DialogFooter>
    ) : (
      footerChildren
    );

  const dialogChildren = isStructuredDialog ? (
    <>
      {trigger ? <DialogTrigger asChild>{trigger}</DialogTrigger> : null}
      <DialogContent
        className={resolvedContentClassName}
        hasCloseButton={hasCloseButton}
      >
        {headerChildren !== undefined ? (
          <DialogHeader className={headerClassName}>
            {title !== undefined || description !== undefined ? (
              <div className="sr-only">
                {title !== undefined ? (
                  <DialogTitle>{title}</DialogTitle>
                ) : null}
                {description !== undefined ? (
                  <DialogDescription>{description}</DialogDescription>
                ) : null}
              </div>
            ) : null}
            {headerChildren}
          </DialogHeader>
        ) : title !== undefined || description !== undefined ? (
          <DialogHeader className={headerClassName}>
            <div>
              {title !== undefined ? <DialogTitle>{title}</DialogTitle> : null}
              {description !== undefined ? (
                <DialogDescription>{description}</DialogDescription>
              ) : null}
            </div>
          </DialogHeader>
        ) : null}
        {resolvedBodyClassName ? (
          <div className={resolvedBodyClassName}>{children}</div>
        ) : (
          children
        )}
        {resolvedFooter !== undefined ? resolvedFooter : null}
      </DialogContent>
    </>
  ) : (
    children
  );

  return (
    <DialogContext.Provider value={contextValue}>
      <DialogPortalContainerContext.Provider value={portalContainer}>
        {dialogChildren}
      </DialogPortalContainerContext.Provider>
    </DialogContext.Provider>
  );
}

export function DialogTrigger({
  asChild = false,
  ...props
}: ComponentPropsWithoutRef<'button'> & {
  asChild?: boolean;
}) {
  const dialog = useContext(DialogContext);

  if (!dialog) {
    throw new Error('DialogTrigger must be used within Dialog.');
  }

  const Component = asChild ? Slot : TovButton;

  return (
    <Component
      {...props}
      onClick={(event) => {
        (props.onClick as ((nextEvent: typeof event) => void) | undefined)?.(
          event,
        );

        if (!event.defaultPrevented) {
          dialog.setOpen(true);
        }
      }}
    />
  );
}

export function DialogPortal({ children }: { children: ReactNode }) {
  if (typeof document === 'undefined') {
    return null;
  }

  return createPortal(children, document.body);
}

export function DialogOverlay({
  className,
  ...props
}: HTMLAttributes<HTMLDivElement>) {
  const dialog = useContext(DialogContext);

  if (!dialog || !dialog.open) {
    return null;
  }

  return (
    <DialogPortal>
      <div className={cn('tov-dialog__overlay', className)} {...props} />
    </DialogPortal>
  );
}

export function DialogContent({
  children,
  className,
  hasCloseButton,
  style,
  showCloseButton = true,
  ...props
}: HTMLAttributes<HTMLDivElement> & {
  hasCloseButton?: boolean;
  showCloseButton?: boolean;
}) {
  const dialog = useContext(DialogContext);
  const isOpen = Boolean(dialog?.open);
  const resolvedHasCloseButton = hasCloseButton ?? showCloseButton;

  useEffect(() => {
    if (!dialog || !isOpen) {
      return;
    }

    const previousOverflow = document.body.style.overflow;

    document.body.style.overflow = 'hidden';

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        dialog.setOpen(false);
      }
    };

    window.addEventListener('keydown', handleKeyDown);

    return () => {
      document.body.style.overflow = previousOverflow;
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [dialog, isOpen]);

  if (!dialog || !isOpen) {
    return null;
  }

  return (
    <DialogPortal>
      <div
        className="tov-dialog__overlay"
        onClick={() => dialog.setOpen(false)}
        aria-hidden="true"
      />
      <div className="tov-dialog">
        <div
          className="tov-dialog__portal-layer"
          ref={dialog.setPortalContainer}
        />
        <div
          aria-describedby={dialog.descriptionId}
          aria-labelledby={dialog.titleId}
          aria-modal="true"
          className={cn('tov-dialog__content', className)}
          id={dialog.contentId}
          onClick={(event) => event.stopPropagation()}
          role="dialog"
          style={style as CSSProperties | undefined}
          {...props}
        >
          {resolvedHasCloseButton ? (
            <DialogClose asChild>
              <TovIconButton
                aria-label={msg('components.tov.components_tov_close_dialog')}
                className="tov-dialog__close"
                icon="x"
                size="icon-sm"
                type="button"
                variant="ghost"
              />
            </DialogClose>
          ) : null}
          {children}
        </div>
      </div>
    </DialogPortal>
  );
}

export function DialogHeader({
  className,
  ...props
}: HTMLAttributes<HTMLDivElement>) {
  return <div className={cn('tov-dialog__header', className)} {...props} />;
}

export function DialogTitle({
  className,
  ...props
}: HTMLAttributes<HTMLHeadingElement>) {
  const dialog = useContext(DialogContext);

  return (
    <h2
      className={cn('tov-dialog__title', className)}
      id={dialog?.titleId}
      {...props}
    />
  );
}

export function DialogDescription({
  className,
  ...props
}: HTMLAttributes<HTMLParagraphElement>) {
  const dialog = useContext(DialogContext);

  return (
    <p
      className={cn('tov-dialog__description', className)}
      id={dialog?.descriptionId}
      {...props}
    />
  );
}

export function DialogFooter({
  className,
  ...props
}: HTMLAttributes<HTMLDivElement>) {
  return <div className={cn('tov-dialog__footer', className)} {...props} />;
}

function DialogPageFooter({
  className,
  end,
  start,
}: {
  className?: string;
  end?: ReactNode;
  start?: ReactNode;
}) {
  return (
    <div className={cn('tov-dialog__page-footer', className)}>
      <div className="tov-dialog__page-footer-group">{start}</div>
      <div className="tov-dialog__page-footer-group tov-dialog__page-footer-group--end">
        {end}
      </div>
    </div>
  );
}

export function DialogClose({
  asChild = false,
  ...props
}: ComponentPropsWithoutRef<'button'> & {
  asChild?: boolean;
}) {
  const dialog = useContext(DialogContext);

  if (!dialog) {
    throw new Error('DialogClose must be used within Dialog.');
  }

  const Component = asChild ? Slot : TovButton;

  return (
    <Component
      {...props}
      onClick={(event) => {
        (props.onClick as ((nextEvent: typeof event) => void) | undefined)?.(
          event,
        );

        if (!event.defaultPrevented) {
          dialog.setOpen(false);
        }
      }}
    />
  );
}
